import React from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  GetAppOutlined,
  KeyboardArrowDown,
  KeyboardArrowUpOutlined,
} from '@mui/icons-material';
import CopyLink from '../components/CopyLink';
import {
  getOrganizationIdsParam,
  useSupplierSurvey,
} from '../Scope3GoodsAndServices/hooks';
import { useGhgSupplierSurveySummary } from '../hooks';
import SupplierSummaryRow from './Row';
import { TimeRange } from 'src/views/components/Dashboard/Stats';
import { timeConverter } from 'src/utils/timeConverter';
import { ReportingFreq } from 'src/utils/api.interfaces';
import { useBranchesWithCountry } from 'src/utils/branches';
import DelayedTextField from 'src/components/DelayedTextField';
import CountryPicker, {
  CountryPickerOption,
} from 'src/components/CountryPicker';

interface Props {
  organizationId: number;
  reportingPeriod: ReportingFreq;
  timeRange: TimeRange | null;
  branches?: string[];
  organizationIds?: number[];
}

export default function SupplierSummary({
  organizationId,
  reportingPeriod,
  timeRange,
  branches = [],
  organizationIds = [],
}: Props) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'dashboard.supplier-summary-table',
  });
  // The shared CountryPicker's own copy (search/no-results/summary) lives
  // under the `ghg` namespace (frontend/src/locale/*/ghg.json, all five
  // languages) rather than `dashboard.supplier-summary-table` (`{lang}.json`,
  // en/pl only) -- a second, un-prefixed `t` reaches it without touching a
  // locale file outside this spec's own Affected Files.
  const { t: tCommon } = useTranslation();
  const start = timeRange
    ? timeConverter(timeRange?.start, reportingPeriod)
    : undefined;
  const end = timeRange
    ? timeConverter(timeRange?.end, reportingPeriod)
    : undefined;

  const [open, setOpen] = React.useState<boolean>(true);
  const [page, setPage] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [search, setSearch] = React.useState<string>('');
  const [selectedCountries, setSelectedCountries] = React.useState<string[]>(
    []
  );
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');

  // Kraj select options (Goal 3): the caller's branches-with-country,
  // narrowed to the ambient `branches` scope this dashboard is already
  // restricted to -- same derivation SupplierSurveys (C14) uses for its own
  // Kraj/Oddział filters.
  const { branches: allBranchesWithCountry } = useBranchesWithCountry();
  const scopedBranchOptions = React.useMemo(() => {
    if (branches.length === 0) return allBranchesWithCountry;
    const allowed = new Set(branches);
    return allBranchesWithCountry.filter((branch) => allowed.has(branch.name));
  }, [allBranchesWithCountry, branches]);

  const countryOptions: CountryPickerOption[] = React.useMemo(() => {
    const byCode = new Map<string, string>();
    scopedBranchOptions.forEach((branch) => {
      if (branch.country_code) {
        byCode.set(
          branch.country_code,
          branch.country_name || branch.country_code
        );
      }
    });
    return Array.from(byCode.entries())
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scopedBranchOptions]);

  React.useEffect(() => {
    setSelectedCountries((current) => {
      const validCodes = new Set(countryOptions.map((country) => country.code));
      const next = current.filter((code) => validCodes.has(code));
      return next.length === current.length ? current : next;
    });
  }, [countryOptions]);

  // Reset to page 1 whenever anything that changes the underlying query
  // fires -- `search` here is already the DEBOUNCED value (DelayedTextField
  // only calls setSearch after its delay), so this doesn't thrash on every
  // keystroke.
  React.useEffect(() => {
    setPage(1);
  }, [search, selectedCountries, pageSize, sortOrder]);

  // EC-5: an empty selection must send NO `countries` param at all -- not
  // an empty array -- so the legacy no-filter path is used server-side.
  const countriesParam = selectedCountries.length
    ? selectedCountries
    : undefined;
  // "View all" (pageSize -1) skips `page` entirely rather than sending
  // page_size=-1, which the backend would reject (page_size is bounded
  // 10..100) -- the legacy no-`page` shape (C06 Goal 1b) already means
  // "everything, unpaged", exactly what this control has always shown.
  const isViewAll = pageSize === -1;

  // RG 28.08 / E-S4: in "selected companies" mode (no branches, some
  // organizations checked) the selection goes to the server like `branches`
  // does, so the page, the total and the exports below are all the same
  // filtered set. Branch mode sends no `organization_ids` at all.
  const organizationIdsParam = getOrganizationIdsParam(
    branches,
    organizationIds
  );

  // Client report 2026-08-27: the four export buttons downloaded the whole
  // (branch-scoped) set while the table above was narrowed by Kraj/Szukaj --
  // the hook call predated those controls and never received them. The
  // exports now carry the SAME filters the visible table queries with
  // (minus paging: an export is always the full filtered set).
  const { exportCsv, exportAllAnswers, exportPivotCsv, exportWideCsv } =
    useSupplierSurvey(
      organizationId,
      start,
      end,
      branches,
      countriesParam,
      search || undefined,
      undefined,
      undefined,
      organizationIdsParam
    );

  const { items, total: serverTotal } = useGhgSupplierSurveySummary(
    organizationId,
    start,
    end,
    branches,
    countriesParam,
    search || undefined,
    isViewAll ? undefined : page,
    isViewAll ? undefined : pageSize,
    organizationIdsParam
  );

  // BR-2/EC-2: an ambient scope of zero branches AND zero organizations must
  // render empty, never "all data" -- axios drops an empty `branches` array
  // from the querystring, so the request above would otherwise come back
  // unfiltered. This is the only client-side scope check left: whatever the
  // server returns for a non-empty scope is shown as it is (RG 28.08 E-S4
  // removed the per-page filtering by organization id, which left a
  // server `total` that did not match the rows).
  const hasScope = branches.length > 0 || organizationIds.length > 0;
  const scopedItems = hasScope ? items : [];
  let total = 0;
  if (hasScope) {
    total = isViewAll ? items.length : (serverTotal ?? 0);
  }

  // Kolejność still flips the visible order (Goal 3: "controls LOOK
  // identical") -- but now over the current server page only, since the
  // full result set is no longer held client-side.
  const orderedSummaries = React.useMemo(() => {
    if (sortOrder === 'asc') return scopedItems;
    return [...scopedItems].reverse();
  }, [scopedItems, sortOrder]);

  const totalPages = isViewAll ? 1 : Math.ceil(total / pageSize);
  let rangeStart = 0;
  if (total > 0) {
    rangeStart = isViewAll ? 1 : Math.min((page - 1) * pageSize + 1, total);
  }
  const rangeEnd = isViewAll ? total : Math.min(page * pageSize, total);

  return (
    <>
      {/* Search and pagination controls */}
      <Box
        display="flex"
        gap="16px"
        alignItems="center"
        flexWrap="wrap"
        mb="16px"
      >
        <DelayedTextField
          label={t('search', 'Search')}
          variant="outlined"
          size="small"
          onDelayedChange={setSearch}
          sx={{ minWidth: '300px', flex: 1 }}
        />

        <CountryPicker
          options={countryOptions}
          value={selectedCountries}
          onChange={setSelectedCountries}
          label={String(t('country', 'Country'))}
          ariaLabel={String(t('country', 'Country'))}
          allSelectedLabel={String(t('allCountries', 'All countries'))}
          searchPlaceholder={String(
            tCommon('ghg.countryPicker.searchPlaceholder')
          )}
          noResultsText={String(tCommon('ghg.countryPicker.noResults'))}
          selectedSummaryTemplate={String(
            tCommon('ghg.countryPicker.selectedSummary')
          )}
          sx={{ minWidth: '150px' }}
        />

        <FormControl size="small" sx={{ minWidth: '150px' }}>
          <InputLabel>{t('order', 'Order')}</InputLabel>
          <Select
            value={sortOrder}
            label={t('order', 'Order')}
            onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
          >
            <MenuItem value="asc">{t('ascending', 'Ascending')}</MenuItem>
            <MenuItem value="desc">{t('descending', 'Descending')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: '150px' }}>
          <InputLabel>{t('page-size', 'Page size')}</InputLabel>
          <Select
            value={pageSize}
            label={t('page-size', 'Page size')}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={-1}>{t('view-all', 'View all')}</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body2">
          {t('showing-results', 'Showing {{start}}-{{end}} of {{total}}', {
            start: rangeStart,
            end: rangeEnd,
            total,
          })}
        </Typography>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="0%">
                <IconButton size="small" onClick={() => setOpen(!open)}>
                  {open ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDown />}
                </IconButton>
              </TableCell>
              <TableCell>{t('vat-identifier')}</TableCell>
              <TableCell>{t('country', 'Country')}</TableCell>
              <TableCell>{t('branch', 'Branch')}</TableCell>
              <TableCell>{t('reporting-period')}</TableCell>
              <TableCell>{t('registered-at')}</TableCell>
              <TableCell>{t('progress-percent')}</TableCell>
              <TableCell>{t('scope-1-entries')}</TableCell>
              <TableCell>{t('scope-2-entries')}</TableCell>
              <TableCell>{t('esg-questionnaire-submitted-at')}</TableCell>
              <TableCell>
                {t('environment-questionnaire-submitted-at')}
              </TableCell>
              {/* C18 Goal 2: additive status columns, matching the two new
                  TableCells SupplierSummaryRow now renders per row -- see
                  that file's Affected-Files note on why this header row
                  had to grow alongside it. Defaults match the EN locale
                  (RG 28.08 5.1). */}
              <TableCell>
                {t('commitment-status', 'Carrier commitment')}
              </TableCell>
              <TableCell>
                {t('target-status', 'Emission reduction target')}
              </TableCell>
            </TableRow>
          </TableHead>
          {open && (
            <TableBody>
              {orderedSummaries.map((survey) => (
                <SupplierSummaryRow
                  key={`${survey.vat_identifier}-${survey.reporting_period}`}
                  survey={survey}
                />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      {pageSize !== -1 && totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt="16px">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="medium"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <Box display="flex" gap="16px" flexWrap="wrap">
        <Button
          size="large"
          endIcon={<GetAppOutlined />}
          onClick={() => exportCsv.mutate()}
          sx={{ mt: '24px', width: '240px' }}
        >
          {t('download')}
        </Button>
        <Button
          size="large"
          endIcon={<GetAppOutlined />}
          onClick={() => exportAllAnswers.mutate()}
          sx={{ mt: '24px', width: '240px' }}
        >
          {t('download-all-answers')}
        </Button>
        <Button
          size="large"
          endIcon={<GetAppOutlined />}
          onClick={() => exportWideCsv.mutate()}
          sx={{ mt: '24px', width: '240px' }}
        >
          {t('download-wide-csv')}
        </Button>
        <Button
          size="large"
          endIcon={<GetAppOutlined />}
          onClick={() => exportPivotCsv.mutate()}
          sx={{ mt: '24px', width: '240px' }}
        >
          {t('download-pivot-data')}
        </Button>
      </Box>
      <CopyLink organizationId={organizationId} mt="32px" />
    </>
  );
}
